/**
 * CouponCodeField — affiliate/promo code entry for checkout (BL-213).
 *
 * Pure presentation (no i18n, no data layer — Non-Negotiables #9): the consumer
 * owns the validate call (SDK `useValidateCoupon`) and passes already-translated
 * strings + the resolved state. The server decides the discount; this only
 * renders the input, the Apply action, and the applied / error line.
 */

import { cn } from '../lib/utils';

export interface CouponCodeFieldProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired when the user submits the code (Apply button or Enter). */
  onApply: () => void;
  /** Optional clear action; when provided and a code is applied, shows Remove. */
  onRemove?: () => void;
  /** Field label, e.g. "Have a coupon?". */
  label: string;
  placeholder: string;
  applyLabel: string;
  removeLabel?: string;
  /** Translated success line, e.g. "ACME20 · 20% off applied". Presence = applied. */
  appliedText?: string;
  /** Translated error line, e.g. "This coupon has expired." */
  errorText?: string;
  isValidating?: boolean;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function CouponCodeField({
  value,
  onChange,
  onApply,
  onRemove,
  label,
  placeholder,
  applyLabel,
  removeLabel,
  appliedText,
  errorText,
  isValidating = false,
  disabled = false,
  className,
  'data-testid': testId,
}: CouponCodeFieldProps) {
  const applied = !!appliedText;
  const canApply = value.trim().length > 0 && !isValidating && !disabled && !applied;

  return (
    <div className={cn('flex flex-col gap-1.5', className)} data-testid={testId}>
      <label htmlFor="coupon-code-input" className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <div className="flex gap-2">
        <input
          id="coupon-code-input"
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canApply) {
              e.preventDefault();
              onApply();
            }
          }}
          placeholder={placeholder}
          disabled={disabled || applied}
          autoCapitalize="characters"
          spellCheck={false}
          className={cn(
            'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm uppercase tracking-wide',
            'ring-offset-background placeholder:normal-case placeholder:tracking-normal placeholder:text-muted-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-60',
            errorText && 'border-destructive focus-visible:ring-destructive'
          )}
          aria-label={label}
          aria-invalid={!!errorText}
          aria-describedby={errorText ? 'coupon-code-error' : undefined}
        />
        {applied && onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="inline-flex h-10 shrink-0 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium ring-offset-background transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {removeLabel}
          </button>
        ) : (
          <button
            type="button"
            onClick={onApply}
            disabled={!canApply}
            className="inline-flex h-10 shrink-0 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {applyLabel}
          </button>
        )}
      </div>
      {appliedText && <p className="text-xs font-medium text-tone-green">{appliedText}</p>}
      {errorText && (
        <p id="coupon-code-error" className="text-xs font-medium text-destructive-accessible">
          {errorText}
        </p>
      )}
    </div>
  );
}
